// Pipeline view — 8 active stages as vertical sections (no horizontal page scroll).
// Stage moves require confirmation when the move has a business consequence.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, Sparkles } from "lucide-react";
import { STAGE_LABELS, API } from "./SalesCRM";

const ACTIVE = ["new_lead", "qualified", "contacted", "replied", "discovery", "proposal_sent", "negotiation", "won"];
const SECONDARY = ["nurture", "lost", "spam"];
const LOST_REASONS = [["no_response", "No response"], ["no_budget", "No budget"], ["chose_other", "Chose another provider"],
    ["timing", "Timing not suitable"], ["service_mismatch", "Service mismatch"], ["cancelled", "Project cancelled"], ["other", "Other"]];
const PRIO_COLORS = { hot: "bg-red-100 text-red-700", warm: "bg-amber-100 text-amber-800", nurture: "bg-lime-100 text-cafe-olive", low: "bg-neutral-100 text-neutral-500" };

export function MoveDialog({ lead, target, onDone, onCancel }) {
    const [reason, setReason] = useState("no_response");
    const [nurtureReason, setNurtureReason] = useState("");
    const [date, setDate] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const confirm = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/crm/leads/${lead.id}/stage`, {
                stage: target, lost_reason: target === "lost" ? reason : "",
                nurture_reason: nurtureReason, followup_date: date, note });
            toast.success(`Moved to ${STAGE_LABELS[target]}`);
            onDone(data.offer_convert);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Move failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="stage-move-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md p-5">
                <div className="flex items-start gap-2">
                    <AlertCircle size={18} className="text-cafe-terracotta mt-0.5" />
                    <div>
                        <div className="font-medium text-sm">Move <span className="text-cafe-espresso font-semibold">{lead.brand}</span> to {STAGE_LABELS[target]}?</div>
                        <div className="text-xs text-neutral-500 mt-1">Current stage: {STAGE_LABELS[lead.stage] || lead.stage}</div>
                    </div>
                </div>
                {target === "lost" && (
                    <label className="block text-xs text-neutral-600 mt-3">Lost reason (required)
                        <select data-testid="lost-reason-select" value={reason} onChange={(e) => setReason(e.target.value)}
                            className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm">
                            {LOST_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </label>
                )}
                {target === "nurture" && (<>
                    <label className="block text-xs text-neutral-600 mt-3">Nurture reason
                        <input data-testid="nurture-reason-input" value={nurtureReason} onChange={(e) => setNurtureReason(e.target.value)}
                            className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                    </label>
                    <label className="block text-xs text-neutral-600 mt-2">Future follow-up date (required)
                        <input data-testid="nurture-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                            className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                    </label>
                </>)}
                {target === "spam" && <div className="text-xs text-red-700 mt-3">This removes the lead from all active pipeline metrics.</div>}
                <label className="block text-xs text-neutral-600 mt-2">Note (optional)
                    <input data-testid="move-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                        className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                </label>
                <div className="flex justify-end gap-2 mt-4">
                    <button data-testid="move-cancel-btn" onClick={onCancel} className="px-4 py-1.5 text-sm rounded-full border">Cancel</button>
                    <button data-testid="move-confirm-btn" disabled={busy} onClick={confirm}
                        className="px-4 py-1.5 text-sm rounded-full bg-cafe-espresso text-white">
                        {busy ? "Moving…" : "Confirm move"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function LeadCard({ c, onOpen, onMove }) {
    const overdue = c.followup_status === "overdue";
    return (
        <div data-testid={`lead-card-${c.id}`}
            className={`bg-white rounded-xl border p-3 text-left w-full ${overdue ? "border-red-300" : "border-neutral-200"}`}>
            <div className="flex justify-between items-start gap-2">
                <button onClick={() => onOpen(c.id)} className="font-medium text-sm text-cafe-espresso hover:underline text-left"
                    data-testid={`lead-open-${c.id}`}>{c.brand}</button>
                <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full ${PRIO_COLORS[c.priority] || PRIO_COLORS.warm}`}>{c.priority}</span>
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
                {[c.business_type, c.location].filter(Boolean).join(" · ") || "—"}
            </div>
            <div className="text-[11px] text-neutral-500">{(c.lead_source || "").replace(/_/g, " ")}
                {c.value > 0 && <span className="text-cafe-terracotta font-medium"> · RM {Math.round(c.value).toLocaleString()}{c.value_is_estimate ? " (est.)" : ""}</span>}
            </div>
            <div className="text-xs mt-1.5 font-medium text-cafe-olive">→ {c.next_action || "Set next action"}</div>
            <div className={`text-[11px] mt-0.5 ${overdue ? "text-red-600 font-semibold" : c.followup_status === "due_today" ? "text-cafe-terracotta font-medium" : "text-neutral-500"}`}>
                {overdue ? `⚠ Overdue — ${c.followup_date}` : c.followup_status === "due_today" ? "Due today" : c.followup_date ? `Follow up ${c.followup_date}` : "No follow-up date"}
                {c.last_contact && <span className="text-neutral-400"> · last contact {(c.last_contact || "").slice(0, 10)}</span>}
            </div>
            <select data-testid={`lead-move-${c.id}`} value="" onChange={(e) => e.target.value && onMove(c, e.target.value)}
                className="mt-2 w-full border border-neutral-200 rounded-lg px-2 py-1 text-[11px] bg-neutral-50">
                <option value="">Move to stage…</option>
                {[...ACTIVE, ...SECONDARY].filter((s) => s !== c.stage).map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
            <button data-testid={`lead-brain-${c.id}`} onClick={() => onOpen(`${c.id}#brain`)}
                className="mt-1.5 w-full flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-cafe-espresso/30 text-cafe-espresso hover:bg-cafe-espresso hover:text-white transition-colors">
                <Sparkles size={11} /> Analyse with AI Sales Brain
            </button>
        </div>
    );
}

export default function PipelineView({ data, onOpen, onChanged }) {
    const [move, setMove] = useState(null); // {lead, target}
    const [showSecondary, setShowSecondary] = useState(false);
    const [convertOffer, setConvertOffer] = useState(null);

    const handleMove = (lead, target) => setMove({ lead, target });

    return (
        <div className="space-y-3" data-testid="pipeline-view">
            {ACTIVE.map((s) => {
                const cards = data.by_stage[s] || [];
                const val = data.stage_values?.[s] || 0;
                return (
                    <div key={s} className="bg-[#F5F0E6] rounded-xl p-3" data-testid={`stage-section-${s}`}>
                        <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-cafe-espresso uppercase tracking-[0.12em]">{STAGE_LABELS[s]}</span>
                            <span className="text-neutral-500">{cards.length}</span>
                            {val > 0 && <span className="text-cafe-terracotta">RM {Math.round(val).toLocaleString()}</span>}
                        </div>
                        {cards.length ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mt-2">
                                {cards.map((c) => <LeadCard key={c.id} c={c} onOpen={onOpen} onMove={handleMove} />)}
                            </div>
                        ) : <div className="text-[11px] text-neutral-400 mt-1">No leads in this stage.</div>}
                    </div>
                );
            })}
            <button data-testid="toggle-secondary-stages" onClick={() => setShowSecondary(!showSecondary)}
                className="text-xs underline text-neutral-500">
                {showSecondary ? "Hide" : "Show"} Nurture / Lost / Spam ({SECONDARY.reduce((n, s) => n + (data.by_stage[s] || []).length, 0)})
            </button>
            {showSecondary && SECONDARY.map((s) => (
                <div key={s} className="bg-neutral-100 rounded-xl p-3" data-testid={`stage-section-${s}`}>
                    <div className="text-xs font-semibold text-neutral-600 uppercase tracking-[0.12em]">{STAGE_LABELS[s]} · {(data.by_stage[s] || []).length}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mt-2">
                        {(data.by_stage[s] || []).map((c) => <LeadCard key={c.id} c={c} onOpen={onOpen} onMove={handleMove} />)}
                    </div>
                </div>
            ))}
            {move && <MoveDialog lead={move.lead} target={move.target}
                onCancel={() => setMove(null)}
                onDone={(offerConvert) => {
                    const l = move.lead; setMove(null); onChanged();
                    if (offerConvert) setConvertOffer(l);
                }} />}
            {convertOffer && (
                <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="convert-offer-dialog">
                    <div className="bg-white rounded-2xl p-5 max-w-sm w-full">
                        <div className="font-medium text-sm">🎉 {convertOffer.brand} is Won!</div>
                        <div className="text-xs text-neutral-600 mt-1">Convert this lead into a Client Project now?</div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button data-testid="convert-later-btn" onClick={() => setConvertOffer(null)} className="px-4 py-1.5 text-sm rounded-full border">Later</button>
                            <button data-testid="convert-now-btn" onClick={() => { onOpen(convertOffer.id + "#convert"); setConvertOffer(null); }}
                                className="px-4 py-1.5 text-sm rounded-full bg-cafe-espresso text-white">Convert to Client Project</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
